import React, {
  UIEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  WheelEventHandler,
} from "react";
import { secondsSinceDate } from "@utils/dates";

type Return = {
  scrollViewRef: React.RefObject<HTMLDivElement>;
  onWheel: WheelEventHandler<HTMLDivElement>;
  onScroll: UIEventHandler<HTMLDivElement>;
  extraHeightToMakeScrollCaptureWork: number;
  isScrolledToTop: boolean;
  isScrolledToBottom: boolean;
};

export type ScrollScreenSwitcherEventHandlerArgs = {
  lastShiftedScreenAt: Date;
  setLastShiftedScreenAt: React.Dispatch<React.SetStateAction<Date>>;
  lastScrollEventAt: Date;
  setLastScrollEventAt: React.Dispatch<React.SetStateAction<Date>>;
  onShouldGoToPrevScreen?: () => void;
  onShouldGoToNextScreen?: () => void;
};
const useSwipeUpAndDownBetweenScreens = (
  args: ScrollScreenSwitcherEventHandlerArgs,
): Return => {
  const scrollViewRef = useRef<HTMLDivElement>(null);
  const getIsScrolledToTop = useCallback(() => {
    const scrollTop = scrollViewRef.current?.scrollTop || 0;
    return scrollTop <= EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK;
  }, []);
  const getIsScrolledToBottom = useCallback(() => {
    const scrollHeight = scrollViewRef.current?.scrollHeight || 0;
    const scrollTop = scrollViewRef.current?.scrollTop || 0;
    const clientHeight = scrollViewRef.current?.clientHeight || 0;
    return (
      scrollHeight <=
      scrollTop + clientHeight + EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK
    );
  }, []);
  const [isScrolledToTop, setIsScrolledToTop] = useState(getIsScrolledToTop());
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(
    getIsScrolledToBottom(),
  );
  useEffect(() => {
    setIsScrolledToTop(getIsScrolledToTop());
    setIsScrolledToBottom(getIsScrolledToBottom());
    setTimeout(() => {
      setIsScrolledToTop(getIsScrolledToTop());
      setIsScrolledToBottom(getIsScrolledToBottom());
    }, 100);
  }, [getIsScrolledToBottom, getIsScrolledToTop]);
  const onWheel: WheelEventHandler<HTMLDivElement> = useCallback(
    (x) => {
      if (
        secondsSinceDate(args.lastShiftedScreenAt) <
        EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK
      )
        return;
      if (
        secondsSinceDate(args.lastScrollEventAt) <
        EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK
      )
        return;
      if (x.deltaY > 40 && getIsScrolledToBottom()) {
        args.onShouldGoToNextScreen?.();
        args.setLastShiftedScreenAt(new Date());
      } else if (x.deltaY < -40 && getIsScrolledToTop()) {
        args.onShouldGoToPrevScreen?.();
        args.setLastShiftedScreenAt(new Date());
      }
    },
    [args, getIsScrolledToBottom, getIsScrolledToTop],
  );
  const onScroll: UIEventHandler<HTMLDivElement> = useCallback(() => {
    const scrollTop = scrollViewRef.current?.scrollTop || 0;
    setIsScrolledToTop(getIsScrolledToTop());
    setIsScrolledToBottom(getIsScrolledToBottom());
    args.setLastScrollEventAt((prevDate) => {
      if (
        secondsSinceDate(prevDate) < 0.1 ||
        scrollTop <= EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK
      )
        return prevDate;
      return new Date();
    });
  }, [args, getIsScrolledToBottom, getIsScrolledToTop]);
  return useMemo(() => {
    return {
      onWheel,
      onScroll,
      scrollViewRef,
      extraHeightToMakeScrollCaptureWork:
        EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK,
      isScrolledToTop,
      isScrolledToBottom,
    };
  }, [isScrolledToBottom, isScrolledToTop, onScroll, onWheel]);
};

export default useSwipeUpAndDownBetweenScreens;

export const EXTRA_HEIGHT_TO_MAKE_SCROLL_CAPTURE_WORK = 0.5;
